/**
 * Загрузка запроса представителя кредитору (раздел «Должник», 25.10.2026).
 *
 * Из запроса берутся сведения о должнике (ФИО, пол, рождение, СНИЛС,
 * паспорт, адрес регистрации) и о представителе (наименование, ИНН, адрес).
 * Почта представителя не берётся: на судебном этапе она другая (решение
 * заказчика 28.10.2026). Ничего не вносится само: каждое поле показано рядом с тем, что
 * уже есть в деле, и вносится отмеченное. Отмечено заранее то, чего в деле
 * нет; поле, где в деле стоит другое значение, — без отметки.
 */

import { useState } from 'react';
import { composeAddress, type CaseProfile } from '@bd/domain';
import { api, type RequestProposal } from '../../../api.js';
import { matchRegions, type RegionOption } from '../../../components/fields/AddressField.js';
import { DocumentUpload } from './DocumentUpload.js';

interface Row {
  key: string;
  group: 'Должник' | 'Представитель';
  label: string;
  value: string;
  current: string;
  warning?: string;
  apply: (p: CaseProfile) => CaseProfile;
}

const ruDate = (iso: string | null | undefined) => (iso ? iso.split('-').reverse().join('.') : '');
const same = (a: string, b: string) => a.trim().toLowerCase().replace(/\s+/g, ' ') === b.trim().toLowerCase().replace(/\s+/g, ' ');

function buildRows(proposal: RequestProposal, profile: CaseProfile, regions: RegionOption[]): { rows: Row[]; regionCode: string | null } {
  const rows: Row[] = [];
  const d = proposal.debtor;
  const cur = profile.debtor;
  const debtor = (patch: (x: CaseProfile['debtor']) => Partial<CaseProfile['debtor']>) => (p: CaseProfile) => ({
    ...p,
    debtor: { ...p.debtor, ...patch(p.debtor) },
  });
  const nameWarning = d.nameChecked ? undefined : 'Падеж подобран неуверенно — проверьте';

  if (d.last) rows.push({ key: 'last', group: 'Должник', label: 'Фамилия', value: d.last, current: cur.last, warning: nameWarning, apply: debtor(() => ({ last: d.last })) });
  if (d.first) rows.push({ key: 'first', group: 'Должник', label: 'Имя', value: d.first, current: cur.first, warning: nameWarning, apply: debtor(() => ({ first: d.first })) });
  if (d.middle) rows.push({ key: 'middle', group: 'Должник', label: 'Отчество', value: d.middle, current: cur.middle ?? '', warning: nameWarning, apply: debtor(() => ({ middle: d.middle })) });
  if (d.gender) {
    const title = d.gender === 'female' ? 'Женский' : 'Мужской';
    const currentTitle = cur.gender === 'female' ? 'Женский' : cur.gender === 'male' ? 'Мужской' : '';
    rows.push({ key: 'gender', group: 'Должник', label: 'Пол', value: title, current: currentTitle, apply: debtor(() => ({ gender: d.gender })) });
  }
  if (d.birthDate) rows.push({ key: 'birthDate', group: 'Должник', label: 'Дата рождения', value: ruDate(d.birthDate), current: ruDate(cur.birthDate), apply: debtor(() => ({ birthDate: d.birthDate })) });
  if (d.birthPlace) rows.push({ key: 'birthPlace', group: 'Должник', label: 'Место рождения', value: d.birthPlace, current: cur.birthPlace, apply: debtor(() => ({ birthPlace: d.birthPlace! })) });
  if (d.snils) rows.push({ key: 'snils', group: 'Должник', label: 'СНИЛС', value: d.snils, current: cur.snils.replace(/\D/g, ''), apply: debtor(() => ({ snils: d.snils! })) });

  const doc = d.idDocument;
  if (doc) {
    const idDoc = (patch: Partial<CaseProfile['debtor']['idDocument']>) =>
      debtor((x) => ({ idDocument: { ...x.idDocument, kind: 'passport_rf', ...patch } }));
    rows.push({
      key: 'passport',
      group: 'Должник',
      label: 'Паспорт: серия и номер',
      value: `${doc.series} ${doc.number}`,
      current: [cur.idDocument.series, cur.idDocument.number].filter(Boolean).join(' '),
      apply: idDoc({ series: doc.series, number: doc.number }),
    });
    if (doc.issuedBy) rows.push({ key: 'issuedBy', group: 'Должник', label: 'Кем выдан', value: doc.issuedBy, current: cur.idDocument.issuedBy, apply: idDoc({ issuedBy: doc.issuedBy }) });
    if (doc.issuedAt) rows.push({ key: 'issuedAt', group: 'Должник', label: 'Дата выдачи', value: ruDate(doc.issuedAt), current: ruDate(cur.idDocument.issuedAt), apply: idDoc({ issuedAt: doc.issuedAt }) });
    if (doc.departmentCode) rows.push({ key: 'departmentCode', group: 'Должник', label: 'Код подразделения', value: doc.departmentCode, current: cur.idDocument.departmentCode ?? '', apply: idDoc({ departmentCode: doc.departmentCode }) });
  }

  /*
    Адрес регистрации раскладывается на поля. Субъект сверяется со справочником
    судов: подставляется его наименование из справочника — по нему находится суд.
    Не нашёлся однозначно — субъект не заполняется, юрист выберет сам.
  */
  let regionCode: string | null = null;
  if (d.address) {
    const matches = d.address.region ? matchRegions(regions, d.address.region) : [];
    const region = matches.length === 1 ? matches[0]! : null;
    regionCode = region?.code ?? null;
    const address = { ...d.address, region: region?.name ?? undefined, source: 'manual' as const };
    const parsed = { ...address, full: composeAddress(address) || d.address.full };
    rows.push({
      key: 'address',
      group: 'Должник',
      label: 'Адрес регистрации',
      value: d.address.full,
      current: cur.registrationAddress.full,
      warning: region ? undefined : 'Субъект РФ не определён — выберите его в адресе после внесения',
      apply: debtor(() => ({ hasRegistration: true, registrationAddress: parsed })),
    });
  }

  const r = proposal.representative;
  if (r) {
    const rep = profile.representative;
    const representative = (patch: Partial<CaseProfile['representative']>) => (p: CaseProfile) => ({
      ...p,
      representative: { ...p.representative, enabled: true, ...patch },
    });
    rows.push({
      key: 'repName',
      group: 'Представитель',
      label: 'Наименование',
      value: r.name,
      current: rep.name,
      warning: r.nameChecked ? undefined : 'Падеж подобран неуверенно — проверьте',
      apply: representative({ name: r.name }),
    });
    if (r.inn) rows.push({ key: 'repInn', group: 'Представитель', label: 'ИНН', value: r.inn, current: rep.inn, apply: representative({ inn: r.inn }) });
    if (r.address) rows.push({ key: 'repAddress', group: 'Представитель', label: 'Адрес', value: r.address, current: rep.address, apply: representative({ address: r.address }) });
  }

  return { rows, regionCode };
}

export function CreditorRequestImport({
  profile,
  update,
  readOnly,
  regions,
  onRegionDetected,
}: {
  profile: CaseProfile;
  update: (apply: (p: CaseProfile) => CaseProfile) => void;
  readOnly?: boolean;
  regions: RegionOption[];
  onRegionDetected?: (code: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [proposal, setProposal] = useState<RequestProposal | null>(null);
  const [checked, setChecked] = useState<Record<string, boolean>>({});

  const view = proposal ? buildRows(proposal, profile, regions) : null;

  const load = async (file: File) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    setProposal(null);
    try {
      const result = await api.importRequest(profile.caseId, file);
      const { rows } = buildRows(result, profile, regions);
      // Отмечено то, чего в деле нет; другое значение в деле — без отметки
      setChecked(Object.fromEntries(rows.map((row) => [row.key, !row.current || same(row.current, row.value)])));
      setProposal(result);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const apply = () => {
    if (!view) return;
    const chosen = view.rows.filter((row) => checked[row.key]);
    update((p) => chosen.reduce((next, row) => row.apply(next), p));
    if (view.regionCode && chosen.some((row) => row.key === 'address')) onRegionDetected?.(view.regionCode);
    setMessage(`Внесено полей: ${chosen.length}.`);
    setProposal(null);
  };

  return (
    <div className="report-import">
      <DocumentUpload
        label="Загрузить запрос кредитору"
        busyLabel="Читаю запрос…"
        hint="Заявление представителя кредитору о предоставлении копий договоров: из него берутся сведения о должнике и представителе. Файл нигде не сохраняется."
        busy={busy}
        disabled={Boolean(readOnly)}
        onFiles={(files) => void load(files[0]!)}
      />
      {error && <p className="error-text">{error}</p>}
      {message && <p className="hint">{message}</p>}

      {view && proposal && (
        <div className="draft">
          <p>
            <strong>{proposal.title}</strong>
          </p>
          <table className="review-table">
            <thead>
              <tr>
                <th className="review-check" />
                <th className="review-field">Поле</th>
                <th>Из запроса</th>
                <th>Сейчас в деле</th>
              </tr>
            </thead>
            <tbody>
              {view.rows.map((row, i) => (
                <tr key={row.key}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`${row.group}: ${row.label}`}
                      checked={Boolean(checked[row.key])}
                      onChange={(e) => setChecked((c) => ({ ...c, [row.key]: e.target.checked }))}
                    />
                  </td>
                  <td>
                    {(i === 0 || view.rows[i - 1]!.group !== row.group) && <div className="hint">{row.group}</div>}
                    {row.label}
                  </td>
                  <td>
                    {row.value}
                    {row.warning && <div className="warning">{row.warning}</div>}
                  </td>
                  <td className={row.current && !same(row.current, row.value) ? 'warning' : 'hint'}>{row.current || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="toolbar">
            <button type="button" className="primary" disabled={readOnly} onClick={apply}>
              Внести отмеченное
            </button>
            <button type="button" className="link-button" onClick={() => setProposal(null)}>
              Отмена
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
